import type { InquiryResponse } from 'backend/types'
import type { InquiryRecruitmentType, InquiryRole } from 'shared'
import { apiFetch } from '..'

/**
 * 引合作成リクエスト型
 */
export interface CreateInquiryRequest {
  dealId: number
  overview?: string | null
  requirements: string
  salesforceProjectId?: number | null
  contactId?: number | null
  coordinationMemberId?: number | null
  primaryDivisionId?: number | null
  salesMemberId?: number | null
  serviceManagerMemberId?: number | null
  startOn?: string | null
  endOn?: string | null
  estimatedAmount?: number | null
  industryName?: string | null
  station?: string | null
  isOnsite?: boolean
  isOffsite?: boolean
  isRemote?: boolean
  isStartAsap?: boolean
  priority?: 'high' | 'medium' | 'low'
  recruitmentType?: InquiryRecruitmentType | null
}

/**
 * 引合作成レスポンス型
 */
export interface CreateInquiryResponse {
  id: number
  deal_id: number | null
  deal_name: string | null
  salesforce_customer_id: number | null
  customer_name: string | null
  salesforce_project_id: number | null
  ano: string | null
  overview: string | null
  requirements: string
  start_on: string | null
  end_on: string | null
  estimated_amount: number | null
  industry_name: string | null
  station: string | null
  is_onsite: boolean
  is_offsite: boolean
  is_remote: boolean
  is_start_asap: boolean
  priority: 'high' | 'medium' | 'low'
  recruitment_type: InquiryRecruitmentType | null
  inquiry_assign_status_id: number
  created_at: number
  updated_at: number
}

/**
 * 引合作成API
 *
 * @param data - 引合作成データ
 * @returns 作成された引合レスポンス
 */
export async function createInquiry(data: CreateInquiryRequest): Promise<CreateInquiryResponse> {
  return apiFetch<CreateInquiryResponse>('/api/inquiry', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

/**
 * 取引先責任者型
 */
export interface Contact {
  id: number
  name: string
  salesforce_customer_id: number
  email: string | null
  enabled: boolean
}

/**
 * 取引先責任者一覧レスポンス型
 */
export interface GetContactsResponse {
  total: number
  contacts: Contact[]
}

/**
 * 取引先責任者一覧取得API
 *
 * @param salesforceCustomerId - 顧客IDで絞り込み（オプション）
 * @returns 取引先責任者一覧レスポンス
 */
export async function getContacts(salesforceCustomerId?: number): Promise<GetContactsResponse> {
  const params = new URLSearchParams()
  if (salesforceCustomerId !== undefined) {
    params.append('salesforce_customer_id', String(salesforceCustomerId))
  }
  const queryString = params.toString()
  return apiFetch<GetContactsResponse>(`/api/contacts${queryString ? `?${queryString}` : ''}`, {
    method: 'GET',
  })
}

/**
 * 引合更新リクエスト型
 */
export interface UpdateInquiryRequest {
  overview?: string | null
  requirements?: string
  startOn?: string | null
  endOn?: string | null
  estimatedAmount?: number | null
  industryName?: string | null
  station?: string | null
  isOnsite?: boolean
  isOffsite?: boolean
  isRemote?: boolean
  isStartAsap?: boolean
  priority?: 'high' | 'medium' | 'low'
  recruitmentType?: InquiryRecruitmentType | null
  inquiryAssignStatusId?: number
  inquiryCloseReason?: string | null
  salesforceProjectId?: number | null
  contactId?: number | null
  primaryDivisionId?: number | null
  salesMemberId?: number | null
  serviceManagerMemberId?: number | null
  coordinationMemberId?: number | null
}

/**
 * 引合更新API
 *
 * @param inquiryId - 引合ID
 * @param data - 更新データ
 */
export async function updateInquiry(inquiryId: number, data: UpdateInquiryRequest): Promise<InquiryResponse> {
  return apiFetch<InquiryResponse>(`/api/inquiry/${inquiryId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  })
}

/**
 * 引合関連部門追加リクエスト型
 */
export interface CreateInquiryRelatedDivisionRequest {
  divisionId: number
  serviceName?: string | null
}

/**
 * 引合関連部門追加API
 *
 * @param inquiryId - 引合ID
 * @param data - 関連部門追加データ
 */
export async function createInquiryRelatedDivision(
  inquiryId: number,
  data: CreateInquiryRelatedDivisionRequest,
): Promise<void> {
  return apiFetch(`/api/inquiry/${inquiryId}/related-division`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

/**
 * 引合関連部門削除API
 *
 * @param inquiryId - 引合ID
 * @param relatedDivisionId - 関連部門ID
 */
export async function deleteInquiryRelatedDivision(inquiryId: number, relatedDivisionId: number): Promise<void> {
  return apiFetch(`/api/inquiry/${inquiryId}/related-division/${relatedDivisionId}`, {
    method: 'DELETE',
  })
}

/**
 * 部門型
 */
export interface Division {
  id: number
  name: string
  short_name: string | null
  is_assignable: boolean
  enabled: boolean
}

/**
 * 部門一覧レスポンス型
 */
export interface GetDivisionsResponse {
  total: number
  divisions: Division[]
}

/**
 * 部門一覧取得API
 *
 * @returns 部門一覧レスポンス
 */
export async function getDivisions(params?: {
  isEnabled?: boolean
  isAssignable?: boolean
}): Promise<GetDivisionsResponse> {
  const searchParams = new URLSearchParams()
  if (params?.isEnabled !== undefined) searchParams.set('isEnabled', String(params.isEnabled))
  if (params?.isAssignable !== undefined) searchParams.set('isAssignable', String(params.isAssignable))
  const qs = searchParams.toString()
  return apiFetch<GetDivisionsResponse>(`/api/divisions${qs ? `?${qs}` : ''}`, {
    method: 'GET',
  })
}

/**
 * 引合ロールメンバー型
 */
export interface InquiryRoleMember {
  id: number
  employeeNumber: number | null
  name: string
  email: string | null
  divisionId: number | null
  divisionName: string | null
  shortDivisionName: string | null
  role: InquiryRole
  jobRoleId: number | null
  jobRoleName: string | null
}

/**
 * 引合ロールメンバー一覧レスポンス型
 */
export interface GetInquiryRoleMembersResponse {
  total: number
  members: InquiryRoleMember[]
}

/**
 * 引合ロールメンバー一覧取得クエリパラメータ型
 */
export interface GetInquiryRoleMembersParams {
  isSales?: boolean
  isSmg?: boolean
  isManager?: boolean
  offset?: number
  limit?: number
}

/**
 * 引合ロールメンバー一覧取得API
 * sales/smg/manager のフラグで絞り込み可能。未指定時は全ロールを返す。
 *
 * @param params - 絞り込みフラグ・ページネーション
 * @returns 引合ロールメンバー一覧レスポンス
 */
export async function getInquiryRoleMembers(
  params?: GetInquiryRoleMembersParams,
): Promise<GetInquiryRoleMembersResponse> {
  const query = new URLSearchParams()
  if (params?.isSales) query.append('isSales', 'true')
  if (params?.isSmg) query.append('isSmg', 'true')
  if (params?.isManager) query.append('isManager', 'true')
  if (params?.offset !== undefined) query.append('offset', String(params.offset))
  if (params?.limit !== undefined) query.append('limit', String(params.limit))
  const queryString = query.toString()
  return apiFetch<GetInquiryRoleMembersResponse>(`/api/inquiry-role-members${queryString ? `?${queryString}` : ''}`, {
    method: 'GET',
  })
}
